import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
  Chip, Stack, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
  Divider, Toolbar, Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BACKEND_URL from './config';

const STAGE_ORDER = ["define","locate","prepare","confirm","execute","monitor","modify","conclude"];

function dedupe(arr) {
  return Array.from(new Set(arr)).sort((a,b) => a.localeCompare(b));
}

function collectProducts(jobMap) {
  const products = new Set();
  if (!jobMap?.stages) return [];
  for (const stage of Object.keys(jobMap.stages)) {
    const steps = jobMap.stages[stage] || [];
    for (const step of steps) {
      (step.product_workflow_steps || []).forEach(pws => {
        if (pws.product_name) products.add(pws.product_name);
      });
    }
  }
  return Array.from(products).sort((a,b) => a.localeCompare(b));
}

export default function JobMapQA() {
  const [jtbdList, setJtbdList] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [jobMap, setJobMap] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [error, setError] = useState('');

  const [stageFilter, setStageFilter] = useState('all'); // one of 'all' or a single stage
  const [productFilter, setProductFilter] = useState('all'); // one of 'all' or a product name

  // 1) Load JTBD index
  useEffect(() => {
    const fetchIndex = async () => {
      setLoadingList(true);
      setError('');
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${BACKEND_URL}/api/jobmaps/final/index`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load index');
        setJtbdList(data.items || []);
        if ((data.items || []).length > 0) {
          setSelectedKey(data.items[0].jtbd_key);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingList(false);
      }
    };
    fetchIndex();
  }, []);

  // 2) Load selected Job Map
  useEffect(() => {
    const fetchMap = async () => {
      if (!selectedKey) return;
      setLoadingMap(true);
      setError('');
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(`${BACKEND_URL}/api/jobmaps/final/${encodeURIComponent(selectedKey)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load job map');
        setJobMap(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingMap(false);
      }
    };
    fetchMap();
  }, [selectedKey]);

  const products = useMemo(() => collectProducts(jobMap), [jobMap]);

  // 3) Prepare filtered stages for rendering
  const stagesToRender = useMemo(() => {
    if (!jobMap?.stages) return [];
    const ordered = STAGE_ORDER.filter(s => jobMap.stages[s]); // keep order / existing only
    const byStage = [];

    for (const s of ordered) {
      if (stageFilter !== 'all' && s !== stageFilter) continue;
      const steps = (jobMap.stages[s] || []).map(step => {
        // Filter product_workflow_steps by productFilter
        const pws = (step.product_workflow_steps || []).filter(
          it => productFilter === 'all' || (it.product_name || '') === productFilter
        );
        return { ...step, product_workflow_steps: pws };
      }).filter(step => {
        // If product filter applied, hide steps that now have 0 product_workflow_steps?
        // We'll keep them, but you can uncomment the next line to hide:
        // return productFilter === 'all' || (step.product_workflow_steps || []).length > 0;
        return true;
      });

      byStage.push({ stage: s, steps });
    }
    return byStage;
  }, [jobMap, stageFilter, productFilter]);

  const selectedDisplay = useMemo(() => {
    const item = jtbdList.find(x => x.jtbd_key === selectedKey);
    return item?.jtbd_display || selectedKey || '';
  }, [jtbdList, selectedKey]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Final Job Maps (QA)</Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <FormControl fullWidth>
            <InputLabel id="jtbd-select-label">JTBD</InputLabel>
            <Select
              labelId="jtbd-select-label"
              label="JTBD"
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              disabled={loadingList}
            >
              {jtbdList.map(it => (
                <MenuItem key={it.jtbd_key} value={it.jtbd_key}>
                  {it.jtbd_display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="stage-filter-label">Stage</InputLabel>
            <Select
              labelId="stage-filter-label"
              label="Stage"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <MenuItem value="all">All stages</MenuItem>
              {STAGE_ORDER.map(s => (
                <MenuItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 240 }}>
            <InputLabel id="product-filter-label">Product</InputLabel>
            <Select
              labelId="product-filter-label"
              label="Product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
            >
              <MenuItem value="all">All products</MenuItem>
              {products.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => { setStageFilter('all'); setProductFilter('all'); }}
          >
            Reset filters
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {(loadingList || loadingMap) && (
        <Stack alignItems="center" sx={{ my: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }}>Loading…</Typography>
        </Stack>
      )}

      {!loadingList && !loadingMap && jobMap && (
        <Box>
          <Typography variant="h6" gutterBottom>{selectedDisplay}</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Stages shown in canonical order. Steps are solution-agnostic. Product workflows list evidence (“receipts”).
          </Typography>

          {stagesToRender.map(({ stage, steps }) => (
            <Paper key={stage} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {stage[0].toUpperCase() + stage.slice(1)}
              </Typography>
              <Divider sx={{ my: 1 }} />

              {steps.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No steps found for this stage.
                </Typography>
              )}

              {steps.map((step, idx) => (
                <Accordion key={`${stage}-${idx}`} disableGutters sx={{ boxShadow: 'none', border: '1px solid #eee', mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%' }}>
                      <Typography sx={{ fontWeight: 600 }}>{step.name}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {step.description}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip size="small" label={`Satisfaction: ${step.satisfaction_score}/10`} />
                      </Stack>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Step receipts (solution-agnostic evidence) */}
                    {(step.receipts || []).length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Step receipts (evidence)</Typography>
                        {(step.receipts || []).map((r, i) => (
                          <Typography variant="body2" key={i} sx={{ ml: 1 }}>
                            • “{r.quote}” <em>({r.file}{r.timestamp ? ` @ ${r.timestamp}` : ''})</em>
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Product workflows */}
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                      Product workflows ({(step.product_workflow_steps || []).length})
                    </Typography>
                    {(step.product_workflow_steps || []).length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        None for current filters.
                      </Typography>
                    )}
                    {(step.product_workflow_steps || []).map((pws, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip size="small" label={pws.product_name || 'Unknown product'} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {pws.name}
                          </Typography>
                          <Chip size="small" label={`Sat: ${pws.satisfaction_score}/10`} />
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 1 }}>{pws.description}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Receipts</Typography>
                        <Box>
                          {pws.receipt1 && (
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              • “{pws.receipt1.quote}” <em>({pws.receipt1.file}{pws.receipt1.timestamp ? ` @ ${pws.receipt1.timestamp}` : ''})</em>
                            </Typography>
                          )}
                          {pws.receipt2 && (
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              • “{pws.receipt2.quote}” <em>({pws.receipt2.file}{pws.receipt2.timestamp ? ` @ ${pws.receipt2.timestamp}` : ''})</em>
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
